import * as React from 'react'
import { useEffect, useRef, useState, useMemo } from 'react'

import { useTransactionsList } from './hooks'
import { Transaction } from './types'
import {
  InputRef,
  Pagination,
  Table,
  Tag,
  Button,
  Typography,
  Flex,
  Select,
  Input,
  Spin,
  message,
  Tooltip,
} from 'antd'
import {
  SearchOutlined,
  DownOutlined,
  UpOutlined,
  CopyOutlined,
} from '@ant-design/icons'
import { path } from '../../../libs'
import { FilterValue } from 'antd/es/table/interface'
import { ColumnsType } from 'antd/lib/table'
import {
  colorTransactionRequestStatus,
  humanDepositRejectionReason,
  humanTransactionRequestStatus,
  humanWithdrawalRejectionReason,
  transactionRequestDirection,
  transactionRequestStatus,
} from '../../../pages/billing/const'
import { ErrorScreen } from '../../../shared/components/errorScreen/errorScreen'
import {
  getColumnSearchDateProps,
  getColumnSearchProps,
} from '../../../libs/tableFunctions'
import { formatDigits } from '../../../libs/utils'
import { ETransactionTypes } from '../../../pages/billing/deposits/list/types'
import {
  $gatewaysByClient,
  gatewaysByClientMounted,
  getGatewaysByClient,
} from './model'
import { useUnit } from 'effector-react'
import {
  resizableTitle,
  TableColumnOption,
  useTableColumnSettings,
} from '../../../components/tableColumnSettings'

const { Text } = Typography

type Props = {
  entityId: string
}

const ALL_COLUMNS = [
  { key: 'transactionId', label: 'ID' },
  { key: 'updatedAt', label: 'Updated at' },
  { key: 'direction', label: 'Direction' },
  { key: 'gateway', label: 'Gateway' },
  { key: 'status', label: 'Status' },
  { key: 'amounts', label: 'Amount' },
  { key: 'rejectReason', label: 'Reject reason' },
  { key: 'bank', label: 'Bank' },
  { key: 'clientName', label: 'Client name' },
  { key: 'clientPurse', label: 'Client purse' },
  { key: 'commission', label: 'Commission' },
  { key: 'internalComment', label: 'Internal comment' },
  { key: 'externalId', label: 'External ID' },
  { key: 'managerComment', label: 'Manager comment', defaultVisible: false },
  { key: 'psComment', label: 'PS comment', defaultVisible: false },
  { key: 'operationType', label: 'Operation type', defaultVisible: false },
  { key: 'transaction_type', label: 'Transaction type', defaultVisible: false },
  {
    key: 'clientCommission',
    label: 'Client commission',
    defaultVisible: false,
  },
  {
    key: 'companyCommission',
    label: 'Company commission',
    defaultVisible: false,
  },
]

const humanTransactionType = (value: unknown): string => {
  const rawValue = String(value ?? '')
  const knownValue =
    ETransactionTypes[rawValue as keyof typeof ETransactionTypes]
  if (knownValue) return knownValue

  const extraLabels: Record<string, string> = {
    TRANSACTION_REQUEST_TYPE_INVALID: 'Invalid',
    TRANSACTION_REQUEST_TYPE_DIVIDEND_WITHDRAWAL_CARD:
      'Dividend withdrawal card',
    exchanger_transfer: 'Exchanger transfer',
  }
  if (extraLabels[rawValue]) return extraLabels[rawValue]

  return rawValue
    .replace(/^TRANSACTION_REQUEST_TYPE_/, '')
    .toLowerCase()
    .replace(/_/g, ' ')
}

const STATUS_FILTERS = [
  transactionRequestStatus.new(),
  transactionRequestStatus.pendingManager(),
  transactionRequestStatus.ps(),
  transactionRequestStatus.pendingDestination(),
  transactionRequestStatus.accepted(),
  transactionRequestStatus.rejected(),
  transactionRequestStatus.pendingRollback(),
  transactionRequestStatus.rollback(),
  transactionRequestStatus.invalid(),
  transactionRequestStatus.questions(),
  transactionRequestStatus.noQuestions(),
  transactionRequestStatus.missingInformation(),
  transactionRequestStatus.cancelMissingInformation(),
  transactionRequestStatus.fundsReserved(),
  transactionRequestStatus.fundsUnReserved(),
  transactionRequestStatus.unconfirmed(),
  transactionRequestStatus.vendorError(),
]

export const TransactionList = ({ entityId }: Props) => {
  const {
    transactions,
    transactionsError,
    totalCountOfTransactions,
    loading,
    onChangeTransactionsList,
  } = useTransactionsList()
  const gateways = useUnit($gatewaysByClient)
  const isLoadingGateways = useUnit(getGatewaysByClient.pending)

  const [searchText, setSearchText] = useState<{ [key: string]: string }>({})
  const [orderBy, setOrderBy] = useState('updatedAt')
  const [orderDirection, setOrderDirection] = useState('-')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(100)
  const [filtersState, setFiltersState] = useState<
    Record<string, FilterValue | null>
  >({})
  const searchInput = useRef<InputRef>(null)

  const columnOptions = ALL_COLUMNS as TableColumnOption[]
  const { settings, apply, resizeStart, layout } = useTableColumnSettings({
    storageKey: 'crm:history-columns',
    columns: columnOptions,
    defaultWidths: {
      transactionId: 90,
      updatedAt: 100,
      direction: 70,
      gateway: 160,
      status: 140,
      amounts: 160,
      rejectReason: 180,
      bank: 100,
      clientName: 120,
      clientPurse: 130,
      commission: 150,
      internalComment: 120,
      externalId: 100,
      managerComment: 180,
      psComment: 180,
      operationType: 150,
      transaction_type: 150,
      clientCommission: 140,
      companyCommission: 140,
    },
  })
  const [showMoreFilters, setShowMoreFilters] = useState(false)

  // Extended filters (client-side)
  const [fBank, setFBank] = useState('')
  const [fClientName, setFClientName] = useState('')
  const [fClientPurse, setFClientPurse] = useState('')
  const [fRejectReason, setFRejectReason] = useState('')
  const [fDirection, setFDirection] = useState<string | null>(null)
  const [fStatus, setFStatus] = useState<string[]>([])

  const extFilterCount =
    [fBank, fClientName, fClientPurse, fRejectReason, fDirection].filter(
      Boolean,
    ).length + (fStatus.length > 0 ? 1 : 0)

  const handleTableChange = ({
    currentPage,
    currentPageSize,
    currentOrderBy,
    currentOrderDirection,
    filters,
  }: {
    currentPage?: number
    currentPageSize?: number
    currentOrderBy?: string
    currentOrderDirection?: string
    filters?: Record<string, FilterValue | null>
  }) => {
    if (currentPage) setPage(currentPage)
    if (currentPageSize) setPageSize(currentPageSize)
    if (currentOrderBy) setOrderBy(currentOrderBy)
    if (currentOrderDirection) {
      setOrderDirection(currentOrderDirection === 'descend' ? '-' : '')
    }
    if (filters) setFiltersState(filters)
  }

  useEffect(() => {
    gatewaysByClientMounted({ clientId: entityId })
  }, [entityId])

  useEffect(() => {
    onChangeTransactionsList({
      clientId: entityId,
      limit: pageSize,
      page,
      orderBy,
      orderDirection,
      searchText,
      filters: {
        ...filtersState,
        direction: fDirection ?? null,
        status: fStatus.length > 0 ? fStatus : null,
      },
    })
  }, [
    pageSize,
    page,
    orderBy,
    orderDirection,
    searchText,
    filtersState,
    fDirection,
    fStatus,
    entityId,
  ])

  // Client-side extended filtering (direction now handled by backend)
  const filteredTransactions = useMemo(() => {
    if (!fBank && !fClientName && !fClientPurse && !fRejectReason)
      return transactions
    return transactions.filter((r) => {
      if (
        fBank &&
        !(r.bankName || '').toLowerCase().includes(fBank.toLowerCase())
      )
        return false
      if (
        fClientName &&
        !(r.clientName || '').toLowerCase().includes(fClientName.toLowerCase())
      )
        return false
      if (
        fClientPurse &&
        !(r.clientPurse || '')
          .toLowerCase()
          .includes(fClientPurse.toLowerCase())
      )
        return false
      if (
        fRejectReason &&
        !(r.rejectReason || '')
          .toLowerCase()
          .includes(fRejectReason.toLowerCase())
      )
        return false
      return true
    })
  }, [transactions, fBank, fClientName, fClientPurse, fRejectReason])

  const show = (key: string) => layout.visible.includes(key)

  const copyInfoForSupport = (item: Transaction) => {
    const lines: string[] = []
    lines.push(`ID: ${item.transactionId}`)
    lines.push(`Updated at: ${item.updatedAt}`)
    lines.push(`Amount: ${item.initialAmount} ${item.initialCurrency}`)
    if (item.bankName) lines.push(`Bank: ${item.bankName}`)
    if (item.clientName) lines.push(`Client name: ${item.clientName}`)
    if (item.clientPurse) lines.push(`Client purse: ${item.clientPurse}`)

    navigator.clipboard.writeText(lines.join('\n'))
    message.success('Info copied')
  }

  const columns: ColumnsType<Transaction> = [
    {
      title: '#',
      key: 'transactionId',
      dataIndex: 'transactionId',
      width: 90,
      ...getColumnSearchProps<Transaction>(
        'transactionId',
        setSearchText,
        searchText,
        searchInput,
      ),
      sorter: true,
      render: (v: string, item: Transaction) => {
        let link = ''
        switch ((item as any).operationType) {
          case 'transaction':
            link =
              item.direction === transactionRequestDirection.In()
                ? path.billing.deposit.edit(item.transactionId)
                : path.billing.withdrawal.edit(item.transactionId)
            break
          case 'exchanger_transfer':
            link = path.billing.exchangerTransfer.details(item.transactionId)
            break
        }
        const product = (item as any).product as string | undefined

        return (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Tooltip title="Copy info for support">
                <CopyOutlined
                  style={{
                    fontSize: 10,
                    color: 'var(--crm-text-secondary)',
                    cursor: 'pointer',
                  }}
                  onClick={() => copyInfoForSupport(item)}
                />
              </Tooltip>
              {link ? (
                <a href={link} target="_blank" style={{ fontSize: 18 }}>
                  {v}
                </a>
              ) : (
                <Text style={{ fontSize: 18 }}>{v}</Text>
              )}
              <Tooltip title="Copy ID">
                <CopyOutlined
                  style={{
                    fontSize: 10,
                    color: 'var(--crm-text-secondary)',
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    navigator.clipboard.writeText(v)
                    message.success('ID copied')
                  }}
                />
              </Tooltip>
            </div>
            {product && (
              <span
                style={{
                  fontSize: 9,
                  fontWeight: 700,
                  padding: '1px 4px',
                  borderRadius: 3,
                  background:
                    product === 'headway'
                      ? 'rgba(219,180,84,0.15)'
                      : 'rgba(80,249,172,0.15)',
                  color: product === 'headway' ? '#dbb454' : '#22c55e',
                  border: `1px solid ${
                    product === 'headway'
                      ? 'rgba(219,180,84,0.4)'
                      : 'rgba(80,249,172,0.4)'
                  }`,
                }}
              >
                {product}
              </span>
            )}
          </div>
        )
      },
    },
    ...(show('externalId')
      ? [
          {
            title: 'TID',
            key: 'externalId',
            width: 100,
            ...getColumnSearchProps<Transaction>(
              'externalId',
              setSearchText,
              searchText,
              searchInput,
            ),
            render: (item: Transaction) => {
              const value = item.externalId ?? ''

              return (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <CopyOutlined
                    style={{
                      fontSize: 10,
                      color: 'var(--crm-text-secondary)',
                      cursor: 'pointer',
                      flexShrink: 0,
                    }}
                    onClick={() => {
                      navigator.clipboard.writeText(value)
                      message.success('ID copied')
                    }}
                  />

                  <Tooltip title={value}>
                    <Text
                      style={{
                        display: 'inline-block',
                        maxWidth: 70, // подберите под нужную ширину колонки
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        verticalAlign: 'bottom',
                      }}
                    >
                      {value}
                    </Text>
                  </Tooltip>
                </div>
              )
            },
          } as any,
        ]
      : []),
    {
      title: 'Updated at',
      key: 'updatedAt',
      dataIndex: 'updatedAt',
      width: 100,
      ...getColumnSearchDateProps<Transaction>(
        'updatedAt',
        setSearchText,
        searchText,
        searchInput,
      ),
      render: (v: string) => {
        const [date, time] = (v || '').split(' ')
        return (
          <div style={{ lineHeight: 1.4 }}>
            <div style={{ fontSize: 18 }}>{date}</div>
            <div style={{ fontSize: 18 }}>{time}</div>
          </div>
        )
      },
    },
    ...(show('direction')
      ? [
          {
            title: 'Dir',
            key: 'direction',
            dataIndex: 'direction',
            width: 28,
            render: (item: string) => (
              <Tag
                color={
                  item === transactionRequestDirection.In()
                    ? '#2eb85c'
                    : '#3399ff'
                }
                style={{ fontSize: 18 }}
              >
                {item === transactionRequestDirection.In() ? 'D' : 'W'}
              </Tag>
            ),
          } as any,
        ]
      : []),
    ...(show('gateway')
      ? [
          {
            title: 'Gateway',
            key: 'gateway',
            dataIndex: 'gatewayInternalName',
            width: 160,
            filters: gateways.map((item) => ({
              text: item.internalName,
              value: item.internalName,
            })),
            render: (v: string) => <Text style={{ fontSize: 18 }}>{v}</Text>,
          } as any,
        ]
      : []),
    ...(show('status')
      ? [
          {
            title: 'Status',
            key: 'status',
            dataIndex: 'status',
            width: 70,
            render: (item: string) => (
              <Tag
                color={colorTransactionRequestStatus(item)}
                style={{ fontSize: 12 }}
              >
                {humanTransactionRequestStatus(item)}
              </Tag>
            ),
          } as any,
        ]
      : []),
    ...(show('amounts')
      ? [
          {
            title: 'Amount',
            key: 'amounts',
            width: 160,
            sorter: (a: Transaction, b: Transaction) =>
              Number(a.initialAmount) - Number(b.initialAmount),
            render: (_: any, r: Transaction) => (
              <div style={{ lineHeight: 1.5 }}>
                <Text style={{ fontSize: 18, fontWeight: 500 }}>
                  {formatDigits(Number(r.initialAmount))} {r.initialCurrency}
                </Text>
                {Number(r.clientAmount) > 0 && (
                  <div>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      →{' '}
                    </Text>
                    <Text style={{ fontSize: 18 }}>
                      {formatDigits(Number(r.clientAmount))} {r.clientCurrency}
                    </Text>
                  </div>
                )}
              </div>
            ),
          } as any,
        ]
      : []),
    ...(show('rejectReason')
      ? [
          {
            title: 'Reject reason',
            key: 'rejectReason',
            width: 180,
            render: (_: any, item: Transaction) => {
              const text =
                item.direction === transactionRequestDirection.In()
                  ? humanDepositRejectionReason(item.rejectReason)
                  : humanWithdrawalRejectionReason(item.rejectReason)
              return text ? (
                <Text
                  style={{
                    fontSize: 18,
                    color: 'var(--crm-accent-red, #ef4444)',
                  }}
                >
                  {text}
                </Text>
              ) : null
            },
          } as any,
        ]
      : []),
    ...(show('bank')
      ? [
          {
            title: 'Bank',
            key: 'bank',
            dataIndex: 'bankName',
            width: 100,
            render: (v: string) =>
              v ? <Text style={{ fontSize: 18 }}>{v}</Text> : null,
          } as any,
        ]
      : []),
    ...(show('clientName')
      ? [
          {
            title: 'Client name',
            key: 'clientName',
            dataIndex: 'clientName',
            width: 120,
            render: (v: string) =>
              v ? <Text style={{ fontSize: 18 }}>{v}</Text> : null,
          } as any,
        ]
      : []),
    ...(show('clientPurse')
      ? [
          {
            title: 'Client purse',
            key: 'clientPurse',
            dataIndex: 'clientPurse',
            width: 130,
            ...getColumnSearchProps<Transaction>(
              'clientPurse',
              setSearchText,
              searchText,
              searchInput,
            ),
            render: (v: string) =>
              v ? <Text style={{ fontSize: 18 }}>{v}</Text> : null,
          } as any,
        ]
      : []),
    ...(show('commission')
      ? [
          {
            title: 'Commission',
            key: 'commission',
            width: 150,
            render: (_: any, r: Transaction) => (
              <div style={{ fontSize: 18, lineHeight: 1.6 }}>
                <div>
                  <Text type="secondary" style={{ fontSize: 10 }}>
                    Client:{' '}
                  </Text>
                  {formatDigits(Number(r.clientCommission))}{' '}
                  {r.commissionCurrency}
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: 10 }}>
                    Company:{' '}
                  </Text>
                  {formatDigits(Number(r.companyCommission))}{' '}
                  {r.commissionCurrency}
                </div>
              </div>
            ),
          } as any,
        ]
      : []),
    ...(show('internalComment')
      ? [
          {
            title: 'Comment',
            key: 'internalComment',
            dataIndex: 'internalComment',
            width: 120,
            render: (v: string) =>
              v ? <Text style={{ fontSize: 18 }}>{v}</Text> : null,
          } as any,
        ]
      : []),
    ...(show('managerComment')
      ? [
          {
            title: 'Manager comment',
            key: 'managerComment',
            dataIndex: 'managerComment',
            width: 180,
          } as any,
        ]
      : []),
    ...(show('psComment')
      ? [
          {
            title: 'PS comment',
            key: 'psComment',
            dataIndex: 'psComment',
            width: 180,
          } as any,
        ]
      : []),
    ...(show('operationType')
      ? [
          {
            title: 'Operation type',
            key: 'operationType',
            dataIndex: 'operationType',
            width: 150,
          } as any,
        ]
      : []),
    ...(show('transaction_type')
      ? [
          {
            title: 'Transaction type',
            key: 'transaction_type',
            dataIndex: 'transaction_type',
            width: 150,
            render: (value: string) => humanTransactionType(value),
          } as any,
        ]
      : []),
    ...(show('clientCommission')
      ? [
          {
            title: 'Client commission',
            key: 'clientCommission',
            dataIndex: 'clientCommission',
            width: 140,
          } as any,
        ]
      : []),
    ...(show('companyCommission')
      ? [
          {
            title: 'Company commission',
            key: 'companyCommission',
            dataIndex: 'companyCommission',
            width: 140,
          } as any,
        ]
      : []),
  ]

  const configuredColumns = apply(
    columns.map((column) => {
      const rawColumn = column as any
      const key = String(
        rawColumn.key ?? rawColumn.dataIndex ?? rawColumn.title,
      )
      return {
        ...column,
        title: resizableTitle(rawColumn.title, key, resizeStart),
      }
    }),
  )

  if (![0, 404, null].includes(transactionsError?.code || null)) {
    return (
      <ErrorScreen
        showRetry
        onRetry={() => {
          onChangeTransactionsList({
            clientId: entityId,
            limit: pageSize,
            page,
            orderBy,
            orderDirection,
            searchText,
            filters: filtersState,
          })
        }}
      />
    )
  }

  if (isLoadingGateways) {
    return (
      <Flex justify="center" align="center" style={{ padding: 40 }}>
        <Spin />
      </Flex>
    )
  }

  return (
    <div>
      {/* Filters bar */}
      <div
        style={{
          background: 'var(--crm-bg)',
          border: '1px solid var(--crm-border)',
          borderRadius: 8,
          padding: '12px 14px',
          marginBottom: 10,
        }}
      >
        <Flex gap={8} align="center" wrap="wrap" justify="space-between">
          <Flex gap={8} wrap="wrap" align="center">
            <Button
              size="small"
              type={
                fDirection === transactionRequestDirection.In()
                  ? 'primary'
                  : 'default'
              }
              onClick={() =>
                setFDirection(
                  fDirection === transactionRequestDirection.In()
                    ? null
                    : transactionRequestDirection.In(),
                )
              }
            >
              Deposit
            </Button>
            <Button
              size="small"
              type={
                fDirection === transactionRequestDirection.Out()
                  ? 'primary'
                  : 'default'
              }
              onClick={() =>
                setFDirection(
                  fDirection === transactionRequestDirection.Out()
                    ? null
                    : transactionRequestDirection.Out(),
                )
              }
            >
              Withdrawal
            </Button>

            <Select
              size="small"
              mode="multiple"
              placeholder="Status"
              allowClear
              value={fStatus}
              onChange={(v) => setFStatus(v)}
              style={{ minWidth: 160, maxWidth: 400 }}
              maxTagCount={10}
              options={STATUS_FILTERS.map((s) => ({
                label: humanTransactionRequestStatus(s),
                value: s,
              }))}
            />

            <Button
              size="small"
              type="text"
              icon={showMoreFilters ? <UpOutlined /> : <DownOutlined />}
              onClick={() => setShowMoreFilters((v) => !v)}
            >
              {showMoreFilters ? 'Less filters' : 'More filters'}
              {extFilterCount > 0 && (
                <Tag color="blue" style={{ fontSize: 10, marginLeft: 4 }}>
                  {extFilterCount}
                </Tag>
              )}
            </Button>

            {extFilterCount > 0 && (
              <Button
                size="small"
                type="text"
                onClick={() => {
                  setFBank('')
                  setFClientName('')
                  setFClientPurse('')
                  setFRejectReason('')
                  setFDirection(null)
                  setFStatus([])
                }}
              >
                Reset ({extFilterCount})
              </Button>
            )}
          </Flex>

          {settings}
        </Flex>

        {showMoreFilters && (
          <Flex
            gap={8}
            wrap="wrap"
            style={{
              marginTop: 10,
              paddingTop: 10,
              borderTop: '1px solid var(--crm-border)',
            }}
          >
            <Input
              size="small"
              placeholder="Bank"
              value={fBank}
              onChange={(e) => setFBank(e.target.value)}
              allowClear
              style={{ width: 130 }}
              prefix={
                <SearchOutlined
                  style={{ color: 'var(--crm-text-secondary)' }}
                />
              }
            />
            <Input
              size="small"
              placeholder="Client name"
              value={fClientName}
              onChange={(e) => setFClientName(e.target.value)}
              allowClear
              style={{ width: 150 }}
              prefix={
                <SearchOutlined
                  style={{ color: 'var(--crm-text-secondary)' }}
                />
              }
            />
            <Input
              size="small"
              placeholder="Client purse"
              value={fClientPurse}
              onChange={(e) => setFClientPurse(e.target.value)}
              allowClear
              style={{ width: 150 }}
              prefix={
                <SearchOutlined
                  style={{ color: 'var(--crm-text-secondary)' }}
                />
              }
            />
            <Input
              size="small"
              placeholder="Reject reason"
              value={fRejectReason}
              onChange={(e) => setFRejectReason(e.target.value)}
              allowClear
              style={{ width: 180 }}
              prefix={
                <SearchOutlined
                  style={{ color: 'var(--crm-text-secondary)' }}
                />
              }
            />
          </Flex>
        )}

        <Text
          type="secondary"
          style={{ fontSize: 14, display: 'block', marginTop: 8 }}
        >
          {filteredTransactions.length} of {totalCountOfTransactions}{' '}
          transactions
        </Text>
      </div>

      {totalCountOfTransactions > 100 && (
        <Pagination
          style={{
            marginBottom: 10,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
          current={page}
          pageSize={pageSize}
          total={totalCountOfTransactions}
          showSizeChanger
          pageSizeOptions={[15, 30, 50, 100]}
          showTotal={(total, range) => `${range[0]}–${range[1]} of ${total}`}
          onChange={(p, ps) => {
            setPage(p)
            setPageSize(ps)
          }}
        />
      )}

      {/* Table */}
      <div
        style={{
          background: 'var(--crm-bg)',
          border: '1px solid var(--crm-border)',
          borderRadius: 8,
          overflow: 'hidden',
        }}
      >
        <Table
          dataSource={filteredTransactions}
          columns={configuredColumns}
          size="small"
          bordered
          scroll={{ x: 'max-content' }}
          loading={loading}
          rowKey="transactionId"
          pagination={{
            pageSize,
            showSizeChanger: true,
            pageSizeOptions: [15, 30, 50, 100],
            total: totalCountOfTransactions,
            showTotal: (total, range) => `${range[0]}–${range[1]} of ${total}`,
          }}
          onChange={(pagination, filters, sorter) =>
            handleTableChange({
              currentPage: pagination.current,
              currentPageSize: pagination.pageSize,
              // @ts-ignore
              currentOrderDirection: sorter.order,
              // @ts-ignore
              currentOrderBy: sorter.field,
              filters,
            })
          }
        />
      </div>
    </div>
  )
}
